/**
 * signatureStore — durable on-device retention of manual-checkout signature
 * images (IndexedDB).
 *
 * (2026-07-28) Added as a safety net. Signature images captured at checkout are
 * POSTed to the backend, which is supposed to upload them to S3; the history
 * endpoint then rebuilds a public URL from the docNum. When that server-side
 * upload fails, the image is lost — it lived only transiently in page state and
 * the one-time request — so billing reports show "(signature unavailable)" with
 * no way to recover it. This store keeps a local copy of every signature the PWA
 * submits, keyed by the ticket docNum(s) the backend returns, so the image can
 * always be recovered later regardless of whether the server-side upload works.
 *
 * Disk-space control: at most `maxCount` signatures are retained (see
 * DEFAULT_MAX_SIGNATURES). When a save pushes the count over the limit, the
 * oldest records (by timestamp) are evicted. The limit is configurable per
 * deployment via AppConfig.features.maxStoredSignatures (config.json).
 *
 * This is purely additive/best-effort: persistence never blocks or fails a
 * checkout (callers wrap in try/catch and this module swallows prune errors).
 */

export interface StoredSignature {
  /** Record key: the docNum(s) joined, or a generated fallback when absent. */
  id: string;
  /** Ticket doc number(s) this signature belongs to (one submit → one image). */
  docNums: string[];
  /** RFID(s) checked out in the same submit — aids attribution to a ticket. */
  rfids: string[];
  /** The signature as a PNG data URL ("data:image/png;base64,..."). */
  signature: string;
  /** Epoch milliseconds when the record was stored. */
  timestamp: number;
}

/** Default retention cap when config.json doesn't specify one. */
export const DEFAULT_MAX_SIGNATURES = 100;

/**
 * Pure eviction policy: given all stored records and a max count, return the
 * ids to delete (oldest first) so at most `maxCount` remain. Exposed separately
 * so the disk-space-control logic is unit-testable without IndexedDB.
 *
 * - maxCount <= 0  → evict everything (retention disabled).
 * - count <= max   → evict nothing.
 */
export function idsToEvict(records: StoredSignature[], maxCount: number): string[] {
  if (maxCount <= 0) return records.map((r) => r.id);
  if (records.length <= maxCount) return [];
  const oldestFirst = [...records].sort((a, b) => a.timestamp - b.timestamp);
  return oldestFirst.slice(0, records.length - maxCount).map((r) => r.id);
}

/**
 * Pure lookup builder: docNum → signature data URL, for every stored record.
 *
 * A batch checkout stores ONE record whose id is the docNums joined ("A,B"), so
 * `store.get(docNum)` misses every ticket but the first. This flattens the
 * docNums arrays so any ticket in a batch resolves to its shared image. Exposed
 * separately (like idsToEvict) so it is unit-testable without IndexedDB.
 *
 * When two records claim the same docNum, the newest wins — a re-signed ticket
 * should show the signature that was captured last.
 */
export function mapByDocNum(records: StoredSignature[]): Map<string, string> {
  const newest = new Map<string, { signature: string; timestamp: number }>();
  for (const record of records) {
    for (const docNum of record.docNums || []) {
      const existing = newest.get(docNum);
      if (!existing || record.timestamp > existing.timestamp) {
        newest.set(docNum, { signature: record.signature, timestamp: record.timestamp });
      }
    }
  }
  // forEach rather than spreading the Map: the build targets ES5 without
  // downlevelIteration, so iterating a Map directly won't compile.
  const result = new Map<string, string>();
  newest.forEach((value, docNum) => result.set(docNum, value.signature));
  return result;
}

class SignatureStore {
  private dbName = 'manual-checkout-signatures';
  private dbVersion = 1;
  private storeName = 'signatures';
  private db: IDBDatabase | null = null;

  /** IndexedDB may be absent (private mode, non-browser test env). */
  private hasIndexedDB(): boolean {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  }

  private async initDB(): Promise<IDBDatabase> {
    if (this.db) return this.db;
    if (!this.hasIndexedDB()) throw new Error('IndexedDB is not available');
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(request.result);
      };
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'id' });
          // Indexed so eviction can find the oldest records cheaply.
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  /** All stored signatures (unordered). */
  async getAll(): Promise<StoredSignature[]> {
    const db = await this.initDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([this.storeName], 'readonly');
      const req = tx.objectStore(this.storeName).getAll();
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve((req.result || []) as StoredSignature[]);
    });
  }

  /** Fetch one record by id (docNum join), or undefined. */
  async get(id: string): Promise<StoredSignature | undefined> {
    const db = await this.initDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([this.storeName], 'readonly');
      const req = tx.objectStore(this.storeName).get(id);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve(req.result as StoredSignature | undefined);
    });
  }

  async remove(id: string): Promise<void> {
    const db = await this.initDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([this.storeName], 'readwrite');
      const req = tx.objectStore(this.storeName).delete(id);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve();
    });
  }

  async clear(): Promise<void> {
    const db = await this.initDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([this.storeName], 'readwrite');
      const req = tx.objectStore(this.storeName).clear();
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve();
    });
  }

  /**
   * Persist a signature, then evict oldest records beyond `maxCount`.
   * Uses `put` (upsert) so re-saving the same docNum id is idempotent.
   * The prune step is best-effort: a prune failure must not lose the just-saved
   * signature, so its error is swallowed.
   *
   * @returns the record id that was written, or null when IndexedDB is
   *          unavailable (e.g. private mode / non-browser env) — a quiet no-op,
   *          since this is a best-effort safety net, not a required write.
   */
  async saveSignature(
    entry: Pick<StoredSignature, 'signature'> &
      Partial<Pick<StoredSignature, 'id' | 'docNums' | 'rfids' | 'timestamp'>>,
    maxCount: number = DEFAULT_MAX_SIGNATURES,
  ): Promise<string | null> {
    if (!this.hasIndexedDB()) return null;
    const db = await this.initDB();
    const docNums = entry.docNums || [];
    const record: StoredSignature = {
      id:
        entry.id ||
        (docNums.length
          ? docNums.join(',')
          : `sig-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`),
      docNums,
      rfids: entry.rfids || [],
      signature: entry.signature,
      timestamp: entry.timestamp ?? Date.now(),
    };
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction([this.storeName], 'readwrite');
      const req = tx.objectStore(this.storeName).put(record);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve();
    });
    try {
      await this.pruneToLimit(maxCount);
    } catch {
      // Reason: the signature is already durably stored; failing to prune is a
      // disk-hygiene concern, not a data-loss one, so never surface it.
    }
    return record.id;
  }

  /**
   * Evict oldest records so at most `maxCount` remain.
   * @returns number of records evicted.
   */
  async pruneToLimit(maxCount: number = DEFAULT_MAX_SIGNATURES): Promise<number> {
    const all = await this.getAll();
    const evict = idsToEvict(all, maxCount);
    for (const id of evict) {
      // eslint-disable-next-line no-await-in-loop
      await this.remove(id);
    }
    return evict.length;
  }
}

// Singleton, mirroring offlineManager's shape.
export const signatureStore = new SignatureStore();
